import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Mail, MessageSquare, Lock, AlertTriangle, ArrowLeft, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SuperAdmin "Forgot password" — EITHER-channel flow.
 * Three stages:
 *   1. `request`  → enter email + pick channel → POST /forgot-password/initiate
 *   2. `verify`   → enter the single OTP + new password → POST /forgot-password/verify
 *   3. `done`     → success card + link back to /admin/login
 */
export function AdminForgotPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('request');
  const [submitting, setSubmitting] = useState(false);

  // Stage 1 inputs
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState('email');

  // Stage 2 inputs
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);

  const initiate = async (e) => {
    e?.preventDefault?.();
    if (!email) return toast.error('Enter your admin email.');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/auth/forgot-password/initiate`, { email, channel });
      setSentInfo(r.data);
      setStage('verify');
      toast.success('OTP dispatched (if the account exists).');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not dispatch OTP.');
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP.');
    if (newPassword.length < 8) return toast.error('New password must be at least 8 characters.');
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/auth/forgot-password/verify`, {
        email,
        channel,
        otp,
        new_password: newPassword,
      });
      setStage('done');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-forgot-password-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'radial-gradient(circle at 20% 20%, #1e293b 0%, #0f172a 40%, #020617 100%)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-4">
            <Shield className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Recover SuperAdmin Access</h1>
          <p className="text-slate-400 text-sm mt-2">We&apos;ll send a one-time code to your recovery contact.</p>
        </div>

        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-7 shadow-2xl">
          {stage === 'request' && (
            <form onSubmit={initiate} className="space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">Admin email</span>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-pw-email"
                    placeholder="admin@example.com"
                    className="pl-9 bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>
              </label>

              <div>
                <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">Send OTP via</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setChannel('email')}
                    data-testid="forgot-pw-channel-email"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      channel === 'email'
                        ? 'border-amber-400/60 bg-amber-500/10 text-amber-200'
                        : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <Mail className="w-4 h-4" /> Recovery email
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannel('whatsapp')}
                    data-testid="forgot-pw-channel-whatsapp"
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                      channel === 'whatsapp'
                        ? 'border-amber-400/60 bg-amber-500/10 text-amber-200'
                        : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" /> WhatsApp
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="forgot-pw-submit"
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send OTP
              </Button>
            </form>
          )}

          {stage === 'verify' && (
            <form onSubmit={verify} className="space-y-4">
              {sentInfo && (
                <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs">
                  <div className="text-slate-300">
                    {sentInfo.sent_to_masked
                      ? (
                        <>
                          OTP sent to{' '}
                          <span className="font-mono text-slate-100">{sentInfo.sent_to_masked}</span>
                        </>
                      )
                      : 'If the account exists with the chosen recovery contact, an OTP has been sent.'}
                  </div>
                  {sentInfo.whatsapp_is_mocked && channel === 'whatsapp' && (
                    <div className="mt-1.5 text-amber-300 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> WhatsApp delivery is mocked — use <code className="font-mono ml-1">123456</code>
                    </div>
                  )}
                </div>
              )}

              <label className="block">
                <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">6-digit OTP</span>
                <Input
                  inputMode="numeric"
                  pattern="\d{6}"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="forgot-pw-otp"
                  className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5 block">New password</span>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="forgot-pw-new-password"
                    placeholder="Min 8 characters"
                    className="pl-9 pr-10 bg-slate-800/50 border-slate-700 text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="forgot-pw-toggle-password"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => { setStage('request'); setOtp(''); setNewPassword(''); }}
                  variant="outline"
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-pw-verify"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Reset password
                </Button>
              </div>
            </form>
          )}

          {stage === 'done' && (
            <div data-testid="forgot-pw-done" className="text-center space-y-4 py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/40">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <div className="text-white font-semibold">Password reset successfully</div>
                <div className="text-xs text-slate-400 mt-1">
                  You can now sign in with your new password.
                </div>
              </div>
              <Button
                onClick={() => navigate('/admin/login')}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                Back to sign in
              </Button>
            </div>
          )}
        </div>

        <div className="mt-5 text-center">
          <Link
            to="/admin/login"
            className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
